import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Zap, Lock, ArrowRight } from 'lucide-react';
import useAuthStore from '../store/authStore';

/**
 * FreePlanWarning - Displays a warning banner for free plan users
 * Shows that they are in simulation mode and encourages upgrade
 */
const FreePlanWarning = ({ className = '', compact = false }) => {
  const navigate = useNavigate();
  const { user } = useAuthStore();

  // Only show for free plan users (simulation_only = true)
  if (!user?.simulation_only) {
    return null;
  }

  const handleUpgrade = () => {
    navigate('/pricing');
  };

  if (compact) {
    return (
      <div className={`bg-red-500/10 border border-red-500/50 rounded-lg p-3 ${className}`}>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
            <span className="text-sm text-red-400 font-medium">
              Simulation mode - Real scan tools are disabled
            </span>
          </div>
          <button
            onClick={handleUpgrade}
            className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white text-xs font-semibold rounded transition-colors flex items-center gap-1"
          >
            <Zap className="w-3 h-3" />
            Passer au Pro
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-gradient-to-r from-red-500/20 via-red-500/10 to-orange-500/20 border-2 border-red-500/50 rounded-xl p-6 ${className}`}>
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-red-500/20 rounded-xl">
            <Lock className="w-8 h-8 text-red-500" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-red-400 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Plan Gratuit - Mode Simulation Uniquement
            </h3>
            <p className="text-gray-400 mt-1 text-sm max-w-xl">
              You are using the free plan. Scans run in <span className="text-red-400 font-semibold">simulation mode</span> with
              demo data. To perform real scans with professional tools
              (Nmap, Nikto, SQLMap, Nuclei, OpenVAS, Metasploit...), upgrade to a paid plan.
            </p>
            <div className="flex flex-wrap gap-2 mt-3">
              {['Nmap', 'Nikto', 'SQLMap', 'Nuclei', 'OpenVAS', 'Metasploit'].map((tool) => (
                <span
                  key={tool}
                  className="px-2 py-0.5 bg-gray-800 text-gray-500 text-xs rounded border border-gray-700 line-through"
                >
                  {tool}
                </span>
              ))}
            </div>
          </div>
        </div>
        <button
          onClick={handleUpgrade}
          className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white font-bold rounded-lg transition-all transform hover:scale-105 shadow-lg shadow-red-500/25"
        >
          <Zap className="w-5 h-5" />
          Passer au Pro
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default FreePlanWarning;
